/**
 * Phase 1 refinement — Workspace Setup Wizard.
 * 7-step provisioning UI (platform_admin only).
 * Strict Realloc design tokens — no gradients, Playfair / Inter / JetBrains Mono.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Check, Copy, Loader2, ShieldCheck, Building2, Users, Layers, Wrench, Globe2 } from "lucide-react";

const cls = (...xs) => xs.filter(Boolean).join(" ");

const STEPS = [
    { id: 1, label: "Customer Identity", icon: Building2 },
    { id: 2, label: "Workspace Admins", icon: Users },
    { id: 3, label: "Enterprise Executives", icon: Users },
    { id: 4, label: "Sub-Org Slicer", icon: Globe2 },
    { id: 5, label: "Values Framework", icon: Layers },
    { id: 6, label: "Tool Configuration", icon: Wrench },
    { id: 7, label: "Review & Provision", icon: ShieldCheck },
];

const VALUES_TEMPLATES = {
    icare:    { name: "ICARE",   dimensions: [{ id: "integrity", name: "Integrity" }, { id: "courage", name: "Courage" }, { id: "accountability", name: "Accountability" }, { id: "respect", name: "Respect" }, { id: "excellence", name: "Excellence" }] },
    trust:    { name: "TRUST",   dimensions: [{ id: "trustworthy", name: "Trustworthy" }, { id: "responsive", name: "Responsive" }, { id: "unified", name: "Unified" }, { id: "service", name: "Service" }, { id: "transparent", name: "Transparent" }] },
    lead:     { name: "LEAD",    dimensions: [{ id: "lead_self", name: "Lead Self" }, { id: "lead_others", name: "Lead Others" }, { id: "lead_outcomes", name: "Lead Outcomes" }, { id: "lead_change", name: "Lead Change" }] },
    generic:  { name: "Professional Conduct", dimensions: [{ id: "ethics", name: "Ethics" }, { id: "ownership", name: "Ownership" }, { id: "collaboration", name: "Collaboration" }, { id: "results", name: "Results" }] },
};

export default function WorkspaceSetupWizard() {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const [form, setForm] = useState({
        name: "",
        legal_name: "",
        primary_industry: "Technology",
        company_website: "",
        company_size_range: "500_5000",
        geographic_footprint: "regional",
        subscription_tier: "standard",
        workspace_admins: [{ full_name: "", email: "", job_title: "" }],
        enterprise_executives: [],
        sub_org_slicer_config: { slicer: "departments", slices: [] },
        values_framework: VALUES_TEMPLATES.generic,
        tool_blocklist: [],
        tool_allowlist: [],
        data_residency: "US",
    });

    const update = (patch) => setForm(prev => ({ ...prev, ...patch }));

    const submit = async () => {
        setBusy(true); setError("");
        try {
            const payload = {
                ...form,
                workspace_admins: form.workspace_admins.filter(a => a.email && a.full_name),
                enterprise_executives: form.enterprise_executives.filter(e => e.email && e.full_name),
                values_framework: { ...form.values_framework, floor_threshold_pct: 50, floor_cap: 59 },
            };
            if (!payload.workspace_admins.length) {
                throw new Error("At least one workspace admin is required.");
            }
            const r = await api.post("/v2/admin/workspaces/provision", payload);
            setResult(r.data);
            setStep(8); // Success view
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    const canAdvance = () => {
        if (step === 1) return form.name && form.primary_industry;
        if (step === 2) return form.workspace_admins.some(a => a.full_name && a.email);
        return true;
    };

    return (
        <div className="p-8 max-w-4xl space-y-6" data-testid="workspace-setup-wizard">
            <div>
                <button onClick={() => navigate("/admin")} className="text-xs text-gray-500 hover:text-white inline-flex items-center gap-1 mb-2">
                    <ChevronLeft className="w-3.5 h-3.5" /> Back to workspaces
                </button>
                <h1 className="text-2xl font-display font-bold text-white">Provision Workspace</h1>
                <p className="text-sm text-gray-500 mt-1 font-body">7-step setup creates the workspace + admin account in one shot.</p>
            </div>

            {/* Stepper */}
            {step <= 7 && (
                <div className="flex items-center justify-between border-y border-border/40 py-3 gap-1">
                    {STEPS.map(s => (
                        <button key={s.id}
                            onClick={() => s.id < step && setStep(s.id)}
                            data-testid={`step-${s.id}`}
                            title={s.label}
                            className={cls(
                                "flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono uppercase whitespace-nowrap transition-colors",
                                step === s.id ? "text-white" : step > s.id ? "text-emerald-300 cursor-pointer hover:text-emerald-200" : "text-gray-600"
                            )}>
                            {step > s.id ? <Check className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
                            <span>{s.id}. {s.label}</span>
                        </button>
                    ))}
                </div>
            )}

            <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                    className="border border-border/40 bg-card p-6 min-h-[400px]">

                    {step === 1 && <Step1Identity form={form} update={update} />}
                    {step === 2 && <Step2Admins admins={form.workspace_admins} setAdmins={(v) => update({ workspace_admins: v })} />}
                    {step === 3 && <Step3Execs execs={form.enterprise_executives} setExecs={(v) => update({ enterprise_executives: v })} />}
                    {step === 4 && <Step4Slicer cfg={form.sub_org_slicer_config} setCfg={(v) => update({ sub_org_slicer_config: v })} />}
                    {step === 5 && <Step5Values vf={form.values_framework} setVf={(v) => update({ values_framework: v })} />}
                    {step === 6 && <Step6Tools form={form} update={update} />}
                    {step === 7 && <Step7Review form={form} />}
                    {step === 8 && <SuccessView result={result} navigate={navigate} />}
                </motion.div>
            </AnimatePresence>

            {step <= 7 && (
                <div className="flex items-center justify-between">
                    <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
                        data-testid="prev-step-btn"
                        className="px-3 py-2 text-xs border border-border/40 text-gray-400 hover:text-white disabled:opacity-30 inline-flex items-center gap-1">
                        <ChevronLeft className="w-3 h-3" /> Previous
                    </button>
                    {error && <p className="text-xs text-red-300">{error}</p>}
                    {step < 7 ? (
                        <button onClick={() => setStep(s => s + 1)} disabled={!canAdvance()}
                            data-testid="next-step-btn"
                            className="px-3 py-2 text-xs bg-white/10 border border-white/20 text-white hover:bg-white/20 disabled:opacity-40 inline-flex items-center gap-1">
                            Next <ChevronRight className="w-3 h-3" />
                        </button>
                    ) : (
                        <button onClick={submit} disabled={busy}
                            data-testid="provision-btn"
                            className="px-3 py-2 text-xs bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-40 inline-flex items-center gap-2">
                            {busy && <Loader2 className="w-3 h-3 animate-spin" />} Provision Workspace
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------- Step 1 ----------
function Step1Identity({ form, update }) {
    return (
        <div className="space-y-4">
            <H2>Customer Identity</H2>
            <Field label="Display name *">
                <Input value={form.name} onChange={v => update({ name: v })} placeholder="Acme Corp" testid="ws-name" />
            </Field>
            <Field label="Legal name">
                <Input value={form.legal_name} onChange={v => update({ legal_name: v })} placeholder="Acme Corporation, Inc." testid="ws-legal" />
            </Field>
            <Field label="Primary industry *">
                <Select value={form.primary_industry} onChange={v => update({ primary_industry: v })} testid="ws-industry"
                    options={["Technology","Financial Services","Healthcare","Insurance","Manufacturing","Retail","Education","Energy","Other"]} />
            </Field>
            <Field label="Company website">
                <Input value={form.company_website} onChange={v => update({ company_website: v })} placeholder="https://acme.example" testid="ws-website" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
                <Field label="Company size">
                    <Select value={form.company_size_range} onChange={v => update({ company_size_range: v })} testid="ws-size"
                        options={[["under_50","Under 50"],["50_500","50–500"],["500_5000","500–5,000"],["5000_plus","5,000+"]]} />
                </Field>
                <Field label="Geographic footprint">
                    <Select value={form.geographic_footprint} onChange={v => update({ geographic_footprint: v })} testid="ws-geo"
                        options={[["single_country","Single country"],["regional","Regional"],["global","Global"]]} />
                </Field>
            </div>
            <Field label="Subscription tier">
                <Select value={form.subscription_tier} onChange={v => update({ subscription_tier: v })} testid="ws-tier"
                    options={[["standard","Standard (Layer 1)"],["growth","Growth (Layer 2)"],["enterprise","Enterprise (Layer 3)"]]} />
            </Field>
        </div>
    );
}

// ---------- Step 2 ----------
function Step2Admins({ admins, setAdmins }) {
    return (
        <div className="space-y-4">
            <H2>Workspace Admin(s)</H2>
            <P>Operates the workspace day-to-day. Required at least one. Receives a temporary password to be changed on first login.</P>
            {admins.map((a, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border border-border/40 p-3">
                    <div className="col-span-4"><Field label="Full name"><Input value={a.full_name} onChange={v => updateAt(admins, setAdmins, i, "full_name", v)} testid={`admin-name-${i}`} /></Field></div>
                    <div className="col-span-4"><Field label="Email"><Input value={a.email} onChange={v => updateAt(admins, setAdmins, i, "email", v)} testid={`admin-email-${i}`} /></Field></div>
                    <div className="col-span-3"><Field label="Job title"><Input value={a.job_title} onChange={v => updateAt(admins, setAdmins, i, "job_title", v)} placeholder="Director, People Ops" testid={`admin-title-${i}`} /></Field></div>
                    {admins.length > 1 && (
                        <button onClick={() => setAdmins(admins.filter((_, j) => j !== i))} data-testid={`remove-admin-${i}`}
                            className="col-span-1 text-xs text-gray-500 hover:text-red-300">×</button>
                    )}
                </div>
            ))}
            <button onClick={() => setAdmins([...admins, { full_name: "", email: "", job_title: "" }])}
                data-testid="add-admin-btn"
                className="text-xs text-gray-500 hover:text-white font-mono">+ Add another admin</button>
        </div>
    );
}

// ---------- Step 3 ----------
function Step3Execs({ execs, setExecs }) {
    return (
        <div className="space-y-4">
            <H2>Enterprise Executives <span className="text-gray-500 text-xs font-mono">(optional)</span></H2>
            <P>Strategic consumers (CIO/CTO/CHRO). Can be added later by the workspace admin.</P>
            {execs.map((e, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border border-border/40 p-3">
                    <div className="col-span-4"><Field label="Full name"><Input value={e.full_name} onChange={v => updateAt(execs, setExecs, i, "full_name", v)} testid={`exec-name-${i}`} /></Field></div>
                    <div className="col-span-4"><Field label="Email"><Input value={e.email} onChange={v => updateAt(execs, setExecs, i, "email", v)} testid={`exec-email-${i}`} /></Field></div>
                    <div className="col-span-3"><Field label="Persona">
                        <Select value={e.persona || ""} onChange={v => updateAt(execs, setExecs, i, "persona", v)} testid={`exec-persona-${i}`}
                            options={[["","—"],["cto","CTO"],["chro","CHRO"],["coo","COO"],["ceo","CEO"]]} />
                    </Field></div>
                    <button onClick={() => setExecs(execs.filter((_, j) => j !== i))} data-testid={`remove-exec-${i}`}
                        className="col-span-1 text-xs text-gray-500 hover:text-red-300">×</button>
                </div>
            ))}
            <button onClick={() => setExecs([...execs, { full_name: "", email: "", persona: "" }])}
                data-testid="add-exec-btn"
                className="text-xs text-gray-500 hover:text-white font-mono">+ Add executive</button>
        </div>
    );
}

// ---------- Step 4 ----------
function Step4Slicer({ cfg, setCfg }) {
    const [text, setText] = useState((cfg.slices || []).join(", "));
    return (
        <div className="space-y-4">
            <H2>Sub-Org Slicer</H2>
            <P>How does this customer slice their organization for cross-org reporting?</P>
            <Field label="Slicer type">
                <Select value={cfg.slicer} onChange={v => setCfg({ ...cfg, slicer: v })} testid="slicer-type"
                    options={[["departments","Departments"],["business_units","Business units"],["regions","Regions"],["divisions","Divisions"]]} />
            </Field>
            <Field label="Slice names (comma-separated)">
                <Input value={text} onChange={v => { setText(v); setCfg({ ...cfg, slices: v.split(",").map(s => s.trim()).filter(Boolean) }); }}
                    placeholder="Engineering, Sales, Operations" testid="slicer-slices" />
            </Field>
            <p className="text-[11px] text-gray-500 font-mono">{cfg.slices?.length || 0} slices configured</p>
        </div>
    );
}

// ---------- Step 5 ----------
function Step5Values({ vf, setVf }) {
    return (
        <div className="space-y-4">
            <H2>Values Framework</H2>
            <P>Capability methodology §13. Locked once first scoring cycle begins.</P>
            <div className="flex flex-wrap gap-2">
                {Object.entries(VALUES_TEMPLATES).map(([key, t]) => (
                    <button key={key} onClick={() => setVf({ ...vf, name: t.name, dimensions: t.dimensions })}
                        data-testid={`values-template-${key}`}
                        className={cls("px-2.5 py-1 text-xs border", vf.name === t.name ? "border-white text-white" : "border-border/40 text-gray-500 hover:text-white")}>
                        {t.name}
                    </button>
                ))}
            </div>
            <Field label="Framework name">
                <Input value={vf.name} onChange={v => setVf({ ...vf, name: v })} testid="vf-name" />
            </Field>
            <Field label="Dimensions">
                <div className="space-y-2">
                    {(vf.dimensions || []).map((d, i) => (
                        <div key={i} className="flex gap-2 items-center">
                            <Input value={d.name} onChange={v => { const next = [...vf.dimensions]; next[i] = { ...d, name: v, id: v.toLowerCase().replace(/\s+/g, "_") }; setVf({ ...vf, dimensions: next }); }}
                                testid={`vf-dim-${i}`} />
                            <button onClick={() => setVf({ ...vf, dimensions: vf.dimensions.filter((_, j) => j !== i) })}
                                data-testid={`remove-vf-dim-${i}`}
                                className="text-xs text-gray-500 hover:text-red-300">×</button>
                        </div>
                    ))}
                    <button onClick={() => setVf({ ...vf, dimensions: [...(vf.dimensions || []), { id: "", name: "" }] })}
                        data-testid="add-vf-dim-btn"
                        className="text-xs text-gray-500 hover:text-white font-mono">+ Add dimension</button>
                </div>
            </Field>
        </div>
    );
}

// ---------- Step 6 ----------
function Step6Tools({ form, update }) {
    const [block, setBlock] = useState((form.tool_blocklist || []).join(", "));
    const [allow, setAllow] = useState((form.tool_allowlist || []).join(", "));
    return (
        <div className="space-y-4">
            <H2>Initial Tool Configuration</H2>
            <P>Surfaces in the Tool Intelligence Layer when recommending tools per task. Comma-separated names.</P>
            <Field label="Blocked tools">
                <Input value={block} onChange={v => { setBlock(v); update({ tool_blocklist: v.split(",").map(s => s.trim()).filter(Boolean) }); }}
                    placeholder="Bard, Rumored-Tool" testid="tool-blocklist" />
            </Field>
            <Field label="Allowlisted / preferred tools">
                <Input value={allow} onChange={v => { setAllow(v); update({ tool_allowlist: v.split(",").map(s => s.trim()).filter(Boolean) }); }}
                    placeholder="Cursor, ChatGPT, Claude" testid="tool-allowlist" />
            </Field>
        </div>
    );
}

// ---------- Step 7 review ----------
function Step7Review({ form }) {
    const Row = ({ label, value }) => (
        <div className="grid grid-cols-12 py-2 border-b border-border/30">
            <div className="col-span-4 text-xs font-mono text-gray-500 uppercase">{label}</div>
            <div className="col-span-8 text-sm text-white font-mono">{value || "—"}</div>
        </div>
    );
    return (
        <div className="space-y-3">
            <H2>Review and Confirm</H2>
            <div data-testid="review-summary">
                <Row label="Workspace name" value={form.name} />
                <Row label="Industry" value={form.primary_industry} />
                <Row label="Tier" value={form.subscription_tier} />
                <Row label="Workspace admins" value={form.workspace_admins.filter(a => a.email).map(a => `${a.full_name} <${a.email}>`).join(", ")} />
                <Row label="Enterprise execs" value={form.enterprise_executives.filter(e => e.email).map(e => `${e.full_name} (${e.persona || "exec"})`).join(", ")} />
                <Row label="Slicer" value={`${form.sub_org_slicer_config?.slicer || "—"}: ${(form.sub_org_slicer_config?.slices || []).join(", ")}`} />
                <Row label="Values framework" value={`${form.values_framework?.name} — ${(form.values_framework?.dimensions || []).map(d => d.name).join(", ")}`} />
                <Row label="Tool blocklist" value={(form.tool_blocklist || []).join(", ")} />
                <Row label="Tool allowlist" value={(form.tool_allowlist || []).join(", ")} />
            </div>
        </div>
    );
}

// ---------- Success ----------
function SuccessView({ result, navigate }) {
    const copy = (t) => navigator.clipboard.writeText(t);
    return (
        <div className="space-y-5" data-testid="provision-success">
            <div className="flex items-center gap-3 text-emerald-300">
                <ShieldCheck className="w-5 h-5" />
                <h2 className="text-xl font-display">Workspace provisioned</h2>
            </div>
            <p className="text-sm text-gray-400">Distribute the temporary passwords below — admins will be required to change on first login.</p>
            <div className="space-y-3">
                {[...(result?.workspace_admins || []).map(a => ({ ...a, role: "enterprise_admin" })),
                  ...(result?.enterprise_executives || []).map(a => ({ ...a, role: "enterprise_executive" }))].map(u => (
                    <div key={u.id} className="border border-border/40 p-3">
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-white">{u.email}</span>
                            <span className="text-[10px] font-mono text-gray-500 uppercase">{u.role}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 bg-background/50 px-2 py-1 text-xs font-mono text-amber-200">{u.temp_password}</code>
                            <button onClick={() => copy(u.temp_password)} data-testid={`copy-${u.email}`}
                                className="px-2 py-1 text-xs border border-white/20 text-white hover:bg-white/10 inline-flex items-center gap-1">
                                <Copy className="w-3 h-3" /> Copy
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex gap-2">
                <button onClick={() => navigate(`/admin/workspace/${result.id}`)}
                    data-testid="view-workspace-btn"
                    className="px-3 py-2 text-xs border border-white/20 text-white hover:bg-white/10">
                    View workspace →
                </button>
                <button onClick={() => navigate("/admin")} className="px-3 py-2 text-xs text-gray-400 hover:text-white">
                    Back to workspaces
                </button>
            </div>
        </div>
    );
}

// ---------- shared primitives ----------
const updateAt = (arr, set, i, k, v) => { const next = [...arr]; next[i] = { ...next[i], [k]: v }; set(next); };
const H2 = ({ children }) => <h2 className="text-lg font-display text-white">{children}</h2>;
const P = ({ children }) => <p className="text-xs text-gray-500 font-body">{children}</p>;
const Field = ({ label, children }) => (
    <div>
        <label className="text-[10px] font-mono uppercase text-gray-500">{label}</label>
        <div className="mt-1">{children}</div>
    </div>
);
const Input = ({ value, onChange, placeholder, testid }) => (
    <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-background border border-border/40 text-sm text-white px-2 py-1.5 focus:outline-none focus:border-white/30" />
);
const Select = ({ value, onChange, options, testid }) => (
    <select value={value || ""} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-background border border-border/40 text-sm text-white px-2 py-1.5 focus:outline-none focus:border-white/30">
        {options.map(o => Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>)}
    </select>
);
